import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationResult, BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { BehaviorSubject, from, NEVER, Observable, throwError } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';

import { CurrentUser } from '../interfaces';
import { AuthVerifyI } from '../interfaces/auth';
import { ClientConfigService } from './client-config.service';
import { MSALService } from './msal.service';

/**
 * Handles the Azure AD / MSAL authentication flow for machine-to-machine
 * (service account) users who access the portal under the "/service" path.
 *
 * Mirrors the CasService contract (verify, getCurrentUser, redirectToLogin,
 * updateUser) so that AuthService can delegate transparently. Unlike CAS,
 * tokens are obtained via MSAL's silent/interactive acquire flows and
 * passed as Bearer headers rather than relying on HTTP-only cookies.
 *
 * Error handling distinguishes 401 (redirect to login) from 403 (navigate
 * to a dedicated "forbidden" page) because service accounts may exist in
 * Azure AD but lack QIAsphere portal permissions.
 */
@Injectable({
  providedIn: 'root'
})
export class ServiceAccountAuthService {
  private readonly clientConfigService = inject(ClientConfigService);
  private readonly httpClient = inject(HttpClient);
  private readonly msalService = inject(MSALService);
  private readonly router = inject(Router);

  public user$ = new BehaviorSubject<CurrentUser | null>(null);

  /**
   * Set as soon as we initiate a redirect to Microsoft. Subsequent
   * `redirectToLogin` / `acquireTokenRedirect` calls (which can fire once
   * per in-flight HTTP request during bootstrap because the auth interceptor
   * resolves `getAuthBearer` for every request in parallel) become no-ops.
   *
   * Without this guard, the second/third caller trips MSAL's
   * `interaction_in_progress` check, throws `BrowserAuthError`, and leaves
   * the cache flag stuck in `"in_progress"` — every page reload then
   * reproduces the error until the user clears site data manually.
   */
  private redirectInFlight = false;

  public verify(): Observable<AuthVerifyI> {
    return this.getCurrentUser().pipe(
      map(user => {
        this.updateUser(user);
        return { user: this.user$.value };
      }),
      catchError(err => {
        if (err.status === 401) {
          this.redirectToLogin();
        }
        if (err.status === 403) {
          this.router.navigate(['/service/forbidden']);
        }
        const account =
          this.msalService.getInstance().getActiveAccount() ?? this.msalService.getInstance().getAllAccounts()[0];
        return throwError(() => ({ user: account?.username }));
      })
    );
  }

  public getCurrentUser(): Observable<CurrentUser> {
    return this.clientConfigService.ssoUrl().pipe(
      concatMap(url => {
        return this.httpClient.get<CurrentUser>(`${url}/current-user`);
      })
    );
  }

  public redirectToLogin(): void {
    if (this.redirectInFlight) return;
    this.redirectInFlight = true;
    // Pre-emptive cleanup: if a previous redirect aborted mid-flight, the
    // interaction status flag is stuck in localStorage and loginRedirect
    // would immediately reject with `interaction_in_progress`. Clearing it
    // here lets the cold-start path self-heal on the next page load.
    this.msalService.clearStuckInteractionState();
    this.msalService
      .getInstance()
      .loginRedirect({ scopes: ['User.Read'] })
      .catch(err => {
        console.error('[MSAL] loginRedirect failed', err);
        this.redirectInFlight = false;
      });
  }

  public updateUser(user: CurrentUser): void {
    user.userOrganizations.sort((a, b) => (a.organization.name < b.organization.name ? -1 : 1));

    user.qiasphereUser = true;
    this.user$.next(user);
  }

  /**
   * Acquires an Azure AD token, preferring the silent (cached) path.
   * Falls back to interactive redirect when the cached token is expired
   * or consent is required. This is called by the HTTP interceptor to
   * attach the Bearer header to every API request in service-account mode.
   *
   * On a cold start the cache is empty, so we trigger `redirectToLogin()`
   * and return `NEVER` rather than `throwError('No account found')`. The
   * page is about to navigate to Microsoft; surfacing an error would
   * cascade through every concurrent caller of the HTTP interceptor and
   * (a) flood the console, (b) trigger error UIs that flash before the
   * redirect completes, (c) race additional redirect calls that hit MSAL's
   * `interaction_in_progress` guard and leave the cache flag stuck.
   */
  public getToken(): Observable<AuthenticationResult> {
    return from(this.msalService.initialize()).pipe(
      switchMap(() => {
        const instance = this.msalService.getInstance();
        const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
        if (!account) {
          this.redirectToLogin();
          return NEVER;
        }
        return from(instance.acquireTokenSilent({ account, scopes: ['User.Read'] }));
      }),
      catchError(error => {
        if (error instanceof InteractionRequiredAuthError) {
          if (this.redirectInFlight) return NEVER;
          this.redirectInFlight = true;
          const instance = this.msalService.getInstance();
          instance
            .acquireTokenRedirect({
              scopes: ['User.Read'],
              account: instance.getActiveAccount() ?? instance.getAllAccounts()[0]
            })
            .catch(err => {
              console.error('[MSAL] acquireTokenRedirect failed', err);
              this.redirectInFlight = false;
            });
          return NEVER;
        }
        if (error instanceof BrowserAuthError) {
          // Typically `interaction_in_progress` from a stuck flag or a
          // `no_token_request_cache_error`. Clear and restart cleanly via
          // the guarded redirectToLogin path.
          this.msalService.clearStuckInteractionState();
          this.redirectToLogin();
          return NEVER;
        }
        return throwError(() => error);
      })
    );
  }
}
